import localforage from 'localforage';
import { OrderType } from '../types/orders';
import {
  deleteFilesFromDirectory,
  getSpecificFileFromDirectory,
  listFilesWithDates,
} from '../utils/files';
import { photoFingerprint } from './photoFingerprint';
import { ownerForEnqueue, sameOwner, type QueueOwner } from './queueOwner';

const QUEUE_KEY = 'rois_offline_image_upload_queue';
/** Clave de esta cola en localforage: el logout la conserva (spec 2026-10-12). */
export const IMAGE_UPLOAD_QUEUE_STORAGE_KEY = QUEUE_KEY;
const MAX_RETRIES = 5;

/**
 * Directorio OPFS dedicado a blobs de uploads diferidos a Cloudinary.
 *
 * Es CRÍTICO que este directorio sea distinto de los directorios de trabajo
 * (`canvas-action`, `canvas-accident`, `OrderType.Action`, `OrderType.Accident`),
 * porque `removeNewAccidentStorage()` y `removeNewActionStorage()` ejecutan
 * `deleteWholeDirectory(directoryNaming.CANVAS)` al final de cada saveData
 * exitoso (incluido cuando la mutation se encoló offline).
 *
 * Si los blobs pendientes vivieran en `canvas-X`, serían destruidos por el
 * cleanup post-save y el sync manager los daría por perdidos.
 */
export const OFFLINE_PENDING_UPLOADS_DIR = 'offline-pending-uploads';

export type ImageUploadKind = 'sketch' | 'photo';

export interface ImageUploadOrderMetadata {
  indexExpedition: number;
  indexType: number;
  classification: string;
  orderType: OrderType;
}

export interface QueuedImageUpload {
  /**
   * Quién la encoló (spec 2026-10-01 § 6). Opcional: las encoladas antes de
   * esta versión no lo llevan y se envían con la sesión actual, como siempre.
   */
  owner?: QueueOwner;
  id: string;
  kind: ImageUploadKind;
  orderId: string;
  orderType: OrderType;

  // Blob persistido en OPFS bajo (blobDirectory, blobFileName)
  blobDirectory: string;
  blobFileName: string;

  // Metadata para nombrado ROID + record DB
  orderMetadata: ImageUploadOrderMetadata;

  // Sketch-only
  sketchData?: {
    svgContent: string | null;
    fabricJson: string | null;
  };

  // ID del Image record DB que debe borrarse SOLO si el create succeeds.
  // Mantiene la invariante "nunca borrar sin haber creado".
  sketchToRemoveId?: string;

  // Photo-only: sequence (1, 2, 3...) para nombrado consistente
  photoSequence?: number;

  /**
   * Photo-only: huella SHA-256 del contenido (`sha256:<hex>`). Es la IDENTIDAD
   * de la foto en la cola: dos entradas con la misma huella son la misma foto
   * aunque cambie el nombre del fichero o el número. Las entradas encoladas
   * antes de existir este campo no la traen; se calcula al compararlas.
   */
  contentHash?: string;

  timestamp: number;
  retryCount: number;
  status: 'pending' | 'syncing' | 'failed';
  errorMessage?: string;

  /**
   * Upload protocol version (storage migration 2026-05).
   *  1 = legacy: sync manager POSTs the blob directly to Cloudinary.
   *  2 = current: sync manager requests a presigned PUT and uploads to Hetzner.
   * Operations enqueued BEFORE the cutover deploy have this undefined →
   * the sync manager treats `undefined` as 1 so in-flight offline work
   * keeps draining via the path it was created for.
   */
  protocolVersion?: 1 | 2;
}

// Mutex lock to prevent race conditions during rapid async queue updates
let queueLock: Promise<void> = Promise.resolve();

async function withLock<T>(action: () => Promise<T>): Promise<T> {
  const nextLock = queueLock.then(() => action()).catch((err) => {
    console.error('Error in image upload queue lock:', err);
    throw err;
  });
  queueLock = nextLock.then(() => { }).catch(() => { });
  return nextLock;
}

export async function getImageUploadQueue(): Promise<QueuedImageUpload[]> {
  try {
    const data = await localforage.getItem<QueuedImageUpload[]>(QUEUE_KEY);
    return data || [];
  } catch (error) {
    console.error('Failed to get image upload queue from localforage', error);
    return [];
  }
}

export type NewImageUploadOperation = Pick<
  QueuedImageUpload,
  | 'kind'
  | 'orderId'
  | 'orderType'
  | 'blobDirectory'
  | 'blobFileName'
  | 'orderMetadata'
> &
  Partial<
    Pick<
      QueuedImageUpload,
      'sketchData' | 'sketchToRemoveId' | 'photoSequence' | 'contentHash'
    >
  >;

/** Lo que deja en la cola un alta: la entrada que queda y si hay trabajo nuevo. */
interface EnqueueResult {
  /** Id de la entrada que queda en la cola (la que ya estaba si la foto se repetía). */
  id: string;
  /** `true` si entró una entrada o se reactivó una `failed`: hay algo que drenar. */
  changed: boolean;
}

/** Huella del fichero de una entrada, o `null` si no existe o no se puede leer. */
async function fingerprintOfFile(
  directory: string,
  fileName: string,
): Promise<string | null> {
  try {
    const found = await getSpecificFileFromDirectory(directory, fileName);
    return found ? await photoFingerprint(found.file) : null;
  } catch {
    return null;
  }
}

async function fileIsThere(directory: string, fileName: string): Promise<boolean> {
  try {
    return (await getSpecificFileFromDirectory(directory, fileName)) !== null;
  } catch {
    return false;
  }
}

const isSameFile = (
  a: Pick<QueuedImageUpload, 'blobDirectory' | 'blobFileName'>,
  b: Pick<QueuedImageUpload, 'blobDirectory' | 'blobFileName'>,
): boolean =>
  a.blobDirectory === b.blobDirectory && a.blobFileName === b.blobFileName;

async function persistQueue(queue: QueuedImageUpload[]): Promise<void> {
  try {
    await localforage.setItem(QUEUE_KEY, queue);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  } catch (error) {
    console.error(
      'Espacio insuficiente en disco para guardar la subida de imagen offline',
      error
    );
  }
}

/**
 * Alta de una operación en la cola.
 *
 * ── Fotos: la identidad es el CONTENIDO (spec 2026-10-01-photo-outbox-identity) ──
 * Antes la clave de una foto era su posición `(orderId, photoSequence)` y el
 * alta BORRABA el fichero de la entrada «obsoleta». Eso perdió fotos de verdad
 * (30-sep, At.0003016.0045.02: 16 de 19):
 *  - re-encolar la misma foto generaba el MISMO nombre de fichero, así que el
 *    borrado se llevaba el fichero que la entrada nueva iba a usar;
 *  - las ediciones numeran sus fotos desde 1, así que una segunda edición con
 *    la primera aún subiendo sustituía en silencio las fotos de la primera.
 *
 * Ahora:
 *  - la misma foto (misma huella SHA-256) que ya está en la bandeja —pendiente,
 *    subiendo o fallida— NO entra otra vez: se conserva la que hay (si había
 *    fallado, vuelve a pendiente). Así ni se pierde ni se sube dos veces;
 *  - una foto distinta entra aunque comparta número;
 *  - el alta nunca borra el fichero de OTRA entrada. Solo borra el de la nueva
 *    cuando no entra (es suyo y nadie lo referencia);
 *  - sin huella (sin `crypto.subtle`, fichero ilegible) la foto se trata como
 *    única: ante la duda, duplicar antes que perder.
 *
 * ── Croquis: la última edición gana (sin cambios) ──
 * Una sola entrada pendiente por `(orderId, kind='sketch')`: si se edita dos
 * veces sin conexión, dos entradas dejarían refs huérfanos en BD (el CREATE de
 * la 1ª no se compensa con un DELETE). No se tocan las `syncing`: el sync
 * manager las está procesando. Sus ficheros ya tenían nombre único.
 */
async function enqueueOne(op: NewImageUploadOperation): Promise<EnqueueResult> {
  return withLock(async () => {
    const queue = await getImageUploadQueue();
    let contentHash: string | undefined;

    // Dueño calculado una sola vez: sirve al dedupe y al sellado de la entrada.
    const owner = ownerForEnqueue();

    if (op.kind === 'photo') {
      contentHash =
        op.contentHash ??
        (await fingerprintOfFile(op.blobDirectory, op.blobFileName)) ??
        undefined;

      // Las entradas encoladas antes de este cambio no traen huella: se
      // calcula de SU fichero la primera vez que hace falta y se guarda.
      let learnedHashes = false;

      for (const existing of queue) {
        if (existing.kind !== 'photo' || existing.orderId !== op.orderId) {
          continue;
        }
        // Dos entradas nunca comparten fichero: la primera en subir lo
        // borraría y la otra se quedaría sin foto. Mismo fichero = misma foto,
        // haya huella o no.
        const sameFile = isSameFile(existing, op);
        if (!sameFile) {
          // Una foto de OTRO operario no es «la misma foto» aunque el
          // contenido coincida: no se fusiona con la suya ni se enviaría a su
          // nombre (spec 2026-10-01 § 12.4). Mismo fichero sí cuenta (dos
          // entradas nunca comparten fichero). Sin dueño, como antes.
          if (existing.owner && !sameOwner(existing.owner, owner)) continue;
          if (!contentHash) continue;
          if (!existing.contentHash) {
            const learned = await fingerprintOfFile(
              existing.blobDirectory,
              existing.blobFileName,
            );
            if (learned) {
              existing.contentHash = learned;
              learnedHashes = true;
            }
          }
          // Misma huella pero SIN fichero no cuenta: esa entrada ya no puede
          // subir nada, y descartar la nueva perdería la foto.
          const samePhoto =
            existing.contentHash === contentHash &&
            (await fileIsThere(existing.blobDirectory, existing.blobFileName));
          if (!samePhoto) continue;
        }

        if (!sameFile) {
          try {
            await deleteFilesFromDirectory(op.blobDirectory, op.blobFileName);
          } catch {
            // queda huérfano en OPFS; no bloquea
          }
        }
        const reactivated = existing.status === 'failed';
        if (reactivated) {
          existing.status = 'pending';
          existing.retryCount = 0;
          existing.errorMessage = undefined;
        }
        if (reactivated || learnedHashes) await persistQueue(queue);
        console.log(
          `[offlineImageUploadQueue] ya estaba en la bandeja (orderId=${op.orderId}, secuencia=${op.photoSequence}, estado=${existing.status})`
        );
        return { id: existing.id, changed: reactivated };
      }
    } else {
      const obsolete = queue.filter(
        (q) =>
          q.orderId === op.orderId &&
          q.kind === 'sketch' &&
          (q.status === 'pending' || q.status === 'failed')
      );
      for (const old of obsolete) {
        // Nunca el fichero que va a usar la entrada nueva.
        if (isSameFile(old, op)) continue;
        try {
          await deleteFilesFromDirectory(old.blobDirectory, old.blobFileName);
        } catch {
          // ignore — el blob queda como orphan, no bloquea
        }
      }
      if (obsolete.length > 0) {
        const obsoleteIds = new Set(obsolete.map((o) => o.id));
        for (let i = queue.length - 1; i >= 0; i--) {
          if (obsoleteIds.has(queue[i].id)) queue.splice(i, 1);
        }
        console.log(
          `[offlineImageUploadQueue] dedupe: ${obsolete.length} op(s) sketch obsoletas reemplazadas (orderId=${op.orderId})`
        );
      }
    }

    const id = `imgupload_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    queue.push({
      ...op,
      owner,
      ...(contentHash && { contentHash }),
      id,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
      // New operations always use the Hetzner path. Legacy in-flight ops
      // (enqueued pre-cutover) have no field → sync manager defaults to 1.
      protocolVersion: 2,
    });

    await persistQueue(queue);
    return { id, changed: true };
  });
}

/** Alta de una operación. Devuelve el id de la entrada que queda en la cola. */
export async function addImageUploadOperation(
  op: NewImageUploadOperation,
): Promise<string> {
  return (await enqueueOne(op)).id;
}

/**
 * Alta de un lote completo de operaciones.
 *
 * Encolar las 20 fotos de una actuación una a una funciona, pero cada llamada
 * toma el mutex y hace su propia lectura+escritura de localforage, así que el
 * contador del banner parpadea 20 veces mientras el guardado intenta navegar.
 * Este envoltorio existe para que el llamador exprese «esto es un lote» y para
 * tener un punto único donde optimizarlo si hiciera falta.
 *
 * Se apoya en `enqueueOne` a propósito: la identidad de las fotos y la limpieza
 * de ficheros son delicadas y no deben existir por duplicado.
 */
export async function addImageUploadBatch(
  ops: NewImageUploadOperation[],
): Promise<string[]> {
  const ids: string[] = [];
  let changed = 0;
  for (const op of ops) {
    const result = await enqueueOne(op);
    ids.push(result.id);
    if (result.changed) changed++;
  }

  // Dispara el drenado. El auto-sync del banner solo reacciona a `isOnline`,
  // así que encolar ESTANDO ONLINE no despertaba a nadie y las fotos se
  // quedaban esperando a un evento de reconexión que nunca llegaba.
  //
  // Se emite un evento en vez de llamar al sync desde cada `saveData` porque
  // esos ficheros tienen varios puntos de retorno (rama offline, error,
  // éxito): sembrar la llamada en todos es justo la clase de cosa que se
  // olvida en el siguiente que se añada. El banner es el dueño natural del
  // sincronizado — ya lleva el guard de reentrada.
  //
  // Solo si entró o se reactivó algo: re-encolar fotos que ya estaban
  // pendientes no añade trabajo.
  if (changed > 0) {
    window.dispatchEvent(
      new CustomEvent('offline-image-batch-queued', {
        detail: { count: changed },
      }),
    );
  }

  return ids;
}

export async function removeImageUpload(id: string): Promise<void> {
  return withLock(async () => {
    const queue = await getImageUploadQueue();
    const filtered = queue.filter((op) => op.id !== id);
    await localforage.setItem(QUEUE_KEY, filtered);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  });
}

export async function incrementImageUploadRetry(id: string): Promise<void> {
  return withLock(async () => {
    const queue = await getImageUploadQueue();
    const updated = queue.map((op) =>
      op.id === id ? { ...op, retryCount: op.retryCount + 1 } : op
    );
    await localforage.setItem(QUEUE_KEY, updated);
  });
}

export async function updateImageUploadStatus(
  id: string,
  status: QueuedImageUpload['status'],
  errorMessage?: string
): Promise<void> {
  return withLock(async () => {
    const queue = await getImageUploadQueue();
    const updated = queue.map((op) =>
      op.id === id
        ? {
            ...op,
            status,
            ...(errorMessage !== undefined && { errorMessage }),
          }
        : op
    );
    await localforage.setItem(QUEUE_KEY, updated);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  });
}

export async function getImageUploadQueueCount(): Promise<number> {
  const queue = await getImageUploadQueue();
  return queue.length;
}

/**
 * Cuenta de items REINTENTABLES (aún se van a intentar): pending + syncing.
 * Excluye `failed` (poison pills terminales) — esos se cuentan aparte para que
 * el banner los muestre como "con error" y no como "pendiente" normal (que se
 * sincronizaría solo). El bug del "1 pendiente eterno" era contar un `failed`
 * como pendiente cuando el auto-sync lo salta y nunca baja a 0.
 */
export async function getImageUploadPendingCount(): Promise<number> {
  const queue = await getImageUploadQueue();
  return queue.filter((op) => op.status !== 'failed').length;
}

/** Cuenta de items en error terminal (poison pill): requieren reintento manual. */
export async function getImageUploadFailedCount(): Promise<number> {
  const queue = await getImageUploadQueue();
  return queue.filter((op) => op.status === 'failed').length;
}

/**
 * Resetea status='failed' → 'pending' **Y `retryCount` → 0** para todas las
 * operaciones. Se invoca SOLO al pulsar "Sincronizar" manualmente (acción
 * explícita del usuario): le da un presupuesto de reintentos FRESCO para
 * recuperar uploads marcados como fallidos (p.ej. tras una caída 5xx o una
 * ventana sin conexión real). Una entrada cuyo fichero ya no existe NO llega
 * aquí: el sync manager la saca de la cola y deja aviso (spec
 * 2026-10-01-photo-outbox-identity § 5.4).
 *
 * Antes NO reseteaba `retryCount` "para preservar MAX_RETRIES" — pero eso
 * dejaba los items en CALLEJÓN SIN SALIDA: al reintentar volvían a superar el
 * límite y se re-marcaban `failed` al instante (el usuario veía "1 pendiente"
 * imposible de quitar). Un poison pill REAL (4xx / 410) vuelve a
 * fallar igualmente por su CLASIFICACIÓN (no por el contador), así que resetar
 * el contador aquí NO crea bucles: solo desatasca lo que sí es reintentable.
 */
export async function resetFailedImageUploads(): Promise<number> {
  return withLock(async () => {
    const queue = await getImageUploadQueue();
    let reset = 0;
    const updated = queue.map((op) => {
      if (op.status === 'failed') {
        reset++;
        return {
          ...op,
          status: 'pending' as const,
          retryCount: 0,
          errorMessage: undefined,
        };
      }
      return op;
    });
    if (reset > 0) {
      await localforage.setItem(QUEUE_KEY, updated);
      window.dispatchEvent(new CustomEvent('offline-queue-changed'));
    }
    return reset;
  });
}

export async function clearImageUploadQueue(): Promise<void> {
  return withLock(async () => {
    await localforage.removeItem(QUEUE_KEY);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  });
}

/**
 * Edad mínima de un fichero sin entrada para darlo por suelto. Quien encola
 * escribe el fichero ANTES de añadir su entrada (`enqueuePhotosForUpload`, los
 * rescates de los `saveData`), así que un fichero recién escrito sin entrada es
 * lo normal durante unos segundos. Una hora deja ese hueco muy atrás.
 */
export const ORPHAN_MIN_AGE_MS = 60 * 60 * 1000;

/**
 * Borra de `OFFLINE_PENDING_UPLOADS_DIR` los ficheros que ya no son de nadie:
 * ninguna entrada de la cola los usa y llevan más de `ORPHAN_MIN_AGE_MS` sin
 * tocarse. Devuelve cuántos borró.
 *
 * De dónde salen (spec 2026-10-12 § 7): la app muere entre quitar la entrada
 * de una foto ya subida y borrar su fichero, o entre escribir el fichero y
 * encolarlo; y, sobre todo, de los cierres de sesión de antes de la #338, que
 * vaciaban la LISTA de la cola pero no la carpeta. Antes de esta rama solo
 * los barría el logout de la #338, que se llevaba también las fotos que
 * esperaban a subir.
 *
 * 🔴 Ante la duda no borra nada:
 * - la cola se lee SIN `getImageUploadQueue`, que ante un error devuelve `[]`
 *   (y entonces todas las fotos pendientes parecerían sueltas);
 * - solo con IndexedDB: si localforage hubiera caído a otro almacén, la cola
 *   que leería no sería la que tiene las entradas;
 * - un fichero que no se puede mirar o borrar se salta.
 * Va dentro del cerrojo de la cola: nada la cambia mientras se compara.
 */
export async function sweepOrphanPendingUploads(now: number = Date.now()): Promise<number> {
  if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) return 0;
  return withLock(async () => {
    await localforage.ready();
    if (localforage.driver() !== localforage.INDEXEDDB) return 0;
    const queue = await localforage.getItem<QueuedImageUpload[]>(QUEUE_KEY);
    if (queue !== null && !Array.isArray(queue)) return 0;
    // Los nombres de TODAS las entradas, de cualquier dueño y carpeta: un
    // nombre de más solo conserva un fichero.
    const referenced = new Set((queue ?? []).map((op) => op.blobFileName));
    const files = await listFilesWithDates(OFFLINE_PENDING_UPLOADS_DIR);
    let removed = 0;
    for (const file of files) {
      if (referenced.has(file.name)) continue;
      if (now - file.lastModified < ORPHAN_MIN_AGE_MS) continue;
      try {
        await deleteFilesFromDirectory(OFFLINE_PENDING_UPLOADS_DIR, file.name);
        removed += 1;
      } catch {
        // Uno que no se deja borrar no impide los demás.
      }
    }
    return removed;
  });
}

export function hasExceededMaxRetries(op: QueuedImageUpload): boolean {
  return op.retryCount >= MAX_RETRIES;
}

export { MAX_RETRIES };

/**
 * Sella con `owner` las operaciones que no tienen dueño (las encoladas antes de
 * la #331) y devuelve cuántas selló. Lo llama el logout ANTES de soltar la
 * sesión: la cola ya no se borra al salir (spec 2026-10-12), y una operación sin
 * dueño la enviaría el siguiente operario con su token y a su tenant.
 */
export async function sealUnownedOperations(owner: QueueOwner | undefined): Promise<number> {
  if (!owner) return 0;
  return withLock(async () => {
    const queue = await getImageUploadQueue();
    let sealed = 0;
    const updated = queue.map((op): QueuedImageUpload => {
      if (op.owner) return op;
      sealed += 1;
      return { ...op, owner };
    });
    if (sealed > 0) await localforage.setItem(QUEUE_KEY, updated);
    return sealed;
  });
}
